/*
Badge evaluator — pure function that turns a snapshot of (captures,
arbresById, arbreAttrs, speciesInfo) into a BadgeState[] in the same
order as the runtime catalogue (`fullCatalog()`).

Strategy mirrors Android `BadgeEvaluator.kt` v1.2.0 + v1.4.0 : single
chronological pass over captures, accumulating count / distinct
species / distinct remarquables / capturedSks /
capturedRemarquableArbreIdsByArr / extrême-dimension flags. Each
criterion's unlock timestamp is the timestamp of the capture that
flipped the threshold. Progressive badges receive ONE timestamp per
tier they cross.

Nouveautés v1.2.0 :
- Familier genre  : critère « toutes les espèces *identifiées* du genre
  capturées » (genre éligible si ≥ 7 espèces). Mirror
  `evaluateFamilierGenre` Android.
- Raretés exactes : 5 binaires (espece_unique..quintette) basés sur le
  count exact d'individus d'une espèce dans Paris (1..5). Remplace
  l'ancien `espece_rare` (< 100 ind., retiré par parité Android v1.2.0).
- Bonsaï / Jeune pousse : 2 binaires dimension `< 2 m` / `< 10 cm circ`.

Nouveautés v1.4.0 :
- Familier arr    : critère bascule de « couverture des espèces
  recensées » à « couverture des arbres remarquables physiques » de
  l'arr (Set<arbreId>, plus de propagation espèce/genre). Mirror
  Android `evaluateFamilierArr` v1.4.0 + ArrSpeciesIndex.remarquableArbreIdsOf.
  2 arrs sans remarquables (2e, 6e) deviennent structurellement
  inunlockables.
*/

import {
	fullCatalog,
	GENRE_FAMILIER_MIN_SPECIES,
	genreBadgeId,
	arrBadgeId,
	pokedexBadgeId,
	kPokedexThresholds,
	kRarityBadgeId,
	type BadgeDef,
	type BadgeState,
	type BinaryBadgeDef,
	type ProgressiveBadgeDef,
	type BinaryBadgeState,
	type ProgressiveBadgeState,
} from './badge'
import type { Capture } from './types'
import type { ArbreFeature } from './arbresFeatures'
import type { SpeciesEntry } from './speciesInfo'
import {
	allSpecies,
	capturedCountInGenre,
	genreCount,
	genreOf,
	isActiveSpecies,
} from './speciesIndex'
import { arrRemarquableArbreIdsOf } from './arrSpecies'
import { arrKeyIdSlug, type ArrKey } from './arrKey'
import { arrFor, heightMFor, circCmFor } from './arbreAttrs'

export interface EvaluatorInput
{
	captures: Capture[]
	arbresById: Map<number, ArbreFeature>  // for the remarquable check; only the ids we need are queried
	speciesInfo: Map<number, SpeciesEntry>  // for the rareté exacte (count == 1..5)
}

const kGeantHeightMeters: number = 30  // hauteur > 30 → Géant
const kBonsaiHeightMeters: number = 2  // hauteur < 2 → Bonsaï
const kVieuxSageCircCm: number = 400  // circ > 400 → Vieux sage
const kJeunePousseCircCm: number = 10  // circ < 10 → Jeune pousse

/*
Evaluate every badge in catalogue order. Each output entry is either a
BinaryBadgeState or a ProgressiveBadgeState (discriminated by `kind`).
Captures must already be sorted oldest-first — that's the contract
`listCaptures()` provides.

Le caller doit avoir chargé : speciesIndex, genreInfo, arrSpecies,
arbreAttrs avant d'appeler cette fonction (les helpers retournent des
fallbacks neutres sinon, mais l'éval est silencieusement incomplète).
*/
export function evaluateBadges(inInput: EvaluatorInput): BadgeState[]
{
	const vAcc = newAccumulator()  // running stats updated capture-by-capture
	const vBinaryUnlocks: Map<string, number> = new Map()  // binary id -> first ts that unlocked it
	// Progressive id -> per-tier first-cross ts. Indexed in lockstep with
	// the def.tiers array so we can pair them when materialising states.
	const vCatalog = fullCatalog()
	const vProgressiveUnlocks: Map<string, (number | null)[]> = new Map()
	for (const vDef of vCatalog)
	{
		if (vDef.kind === 'progressive')
			vProgressiveUnlocks.set(vDef.id, vDef.tiers.map(() => null))
	}

	// Pokédex v1.4.0 : pour chaque palier N, set des sks actifs (identifiés +
	// pokedexNumber dans [1..N]). Précalculé une fois — comparaison capture-
	// par-capture en O(target.size).
	const vPokedexTargets = buildPokedexTargets()

	for (const vCap of inInput.captures)
	{
		vAcc.count += 1
		vAcc.species.add(vCap.speciesIndex)
		const vArbre = inInput.arbresById.get(vCap.arbreId)  // for remarquable check; null is fine
		if (vArbre?.remarquable) vAcc.remarquables.add(vCap.arbreId)

		// Premiere capture — débloque dès la 1re capture, mirror Android.
		checkBinary(vBinaryUnlocks, 'premiere_capture', true, vCap.timestamp)

		// Raretés exactes 1..5 — calées sur l'espèce, exclut les
		// captures de remarquables (parité Android v1.2.0
		// `if (!capture.remarquable)`).
		if (!vArbre?.remarquable)
		{
			const vEntry = inInput.speciesInfo.get(vCap.speciesIndex)
			const vCount = vEntry?.stats.count
			if (typeof vCount === 'number')
			{
				const vRarityId = kRarityBadgeId[vCount]
				if (vRarityId) checkBinary(vBinaryUnlocks, vRarityId, true, vCap.timestamp)
			}
		}

		// Dimensions — via le sidecar arbre-attrs.json (remplace
		// l'ancien arbres-details.json, retiré côté Android v1.3.2).
		const vHauteur = heightMFor(vCap.arbreId)
		if (typeof vHauteur === 'number')
		{
			if (vHauteur > kGeantHeightMeters) checkBinary(vBinaryUnlocks, 'geant', true, vCap.timestamp)
			if (vHauteur < kBonsaiHeightMeters) checkBinary(vBinaryUnlocks, 'bonsai', true, vCap.timestamp)
		}
		const vCirc = circCmFor(vCap.arbreId)
		if (typeof vCirc === 'number')
		{
			if (vCirc > kVieuxSageCircCm) checkBinary(vBinaryUnlocks, 'vieux_sage', true, vCap.timestamp)
			if (vCirc < kJeunePousseCircCm) checkBinary(vBinaryUnlocks, 'jeune_pousse', true, vCap.timestamp)
		}

		// Familier genre — couverture des espèces identifiées du genre.
		vAcc.capturedSks.add(vCap.speciesIndex)
		evaluateFamilierGenre(vBinaryUnlocks, vCap.speciesIndex, vAcc.capturedSks, vCap.timestamp)

		// Pokédex v1.4.0 — couverture des sks actifs par seuil de
		// pokedexNumber, hors remarquables (sémantique Catalogue Arboretum
		// par fréquence).
		if (!vArbre?.remarquable)
		{
			vAcc.capturedNonRemarquableSks.add(vCap.speciesIndex)
			evaluatePokedex(vBinaryUnlocks, vPokedexTargets, vAcc.capturedNonRemarquableSks, vCap.timestamp)
		}

		// Familier arr v1.4.0 — couverture des arbres remarquables physiques.
		// Seuls les remarquables capturés contribuent ; le set par arr s'accumule
		// puis on déclenche evaluateFamilierArr après ajout (comparaison
		// Set<arbreId> ⊇ remarquableArbreIdsOf(arr)).
		const vArr = arrFor(vCap.arbreId)
		if (vArr && vArr.kind !== 'other' && vArbre?.remarquable)
		{
			const vSlug = arrKeyIdSlug(vArr)
			let vSeen = vAcc.capturedRemarquableArbreIdsByArr.get(vSlug)
			if (!vSeen) { vSeen = new Set<number>(); vAcc.capturedRemarquableArbreIdsByArr.set(vSlug, vSeen) }
			vSeen.add(vCap.arbreId)
			evaluateFamilierArr(vBinaryUnlocks, vArr, vSeen, vCap.timestamp)
		}

		// Progressive criteria — for each tier of each progressive badge,
		// record the timestamp the first time the running count crosses
		// the tier threshold.
		checkProgressive(vCatalog, vProgressiveUnlocks, 'marcheur', vAcc.count, vCap.timestamp)
		checkProgressive(vCatalog, vProgressiveUnlocks, 'botaniste', vAcc.species.size, vCap.timestamp)
		checkProgressive(vCatalog, vProgressiveUnlocks, 'chasseur', vAcc.remarquables.size, vCap.timestamp)
	}

	return vCatalog.map((inDef): BadgeState => {
		if (inDef.kind === 'binary')
		{
			const vState: BinaryBadgeState = {
				kind: 'binary',
				def: inDef as BinaryBadgeDef,
				unlockedAt: vBinaryUnlocks.get(inDef.id) ?? null,
			}
			return vState
		}
		const vDef = inDef as ProgressiveBadgeDef
		const vUnlocks = vProgressiveUnlocks.get(vDef.id) ?? vDef.tiers.map(() => null)
		const vState: ProgressiveBadgeState = {
			kind: 'progressive',
			def: vDef,
			currentCount: progressiveCount(vDef, vAcc),
			tierUnlocks: vUnlocks,
		}
		return vState
	})
}

/*
Familier d'un genre : toutes les espèces identifiées du genre
capturées. N'agit que sur les genres éligibles (genreCount ≥ 7).
Mirror Android `evaluateFamilierGenre`.
*/
function evaluateFamilierGenre(
	inUnlocks: Map<string, number>,
	inSk: number,
	inCapturedSks: ReadonlySet<number>,
	inTs: number,
): void
{
	const vGenre = genreOf(inSk)
	if (!vGenre) return
	const vTarget = genreCount(vGenre)
	if (vTarget < GENRE_FAMILIER_MIN_SPECIES) return
	if (capturedCountInGenre(vGenre, inCapturedSks) === vTarget)
	{
		checkBinary(inUnlocks, genreBadgeId(vGenre), true, inTs)
	}
}

/*
Familier d'un arrondissement v1.4.0 : couverture de tous les arbres
remarquables physiques de l'arr (Set<arbreId>). Comparaison directe
sans propagation espèce/genre — mirror Android v1.4.0
`evaluateFamilierArr`. Les arrs sans remarquables (2e, 6e au tag v1.4.0)
sont skippés silencieusement.
*/
function evaluateFamilierArr(
	inUnlocks: Map<string, number>,
	inArr: ArrKey,
	inSeenRemarquableIds: ReadonlySet<number>,
	inTs: number,
): void
{
	const vTarget = arrRemarquableArbreIdsOf(inArr)
	if (vTarget.size === 0) return
	for (const vId of vTarget) if (!inSeenRemarquableIds.has(vId)) return
	checkBinary(inUnlocks, arrBadgeId(inArr), true, inTs)
}

interface Accumulator
{
	count: number
	species: Set<number>  // distinct sk capturés (identifiés + unknown)
	remarquables: Set<number>  // arbreIds of captured remarquable trees
	capturedSks: Set<number>  // distinct sk capturés — utilisé par Familier genre
	capturedNonRemarquableSks: Set<number>  // distinct sk capturés hors remarquables — Pokédex v1.4.0
	capturedRemarquableArbreIdsByArr: Map<string, Set<number>>  // arrSlug -> Set<arbreId> remarquables capturés
}

function newAccumulator(): Accumulator
{
	return {
		count: 0,
		species: new Set(),
		remarquables: new Set(),
		capturedSks: new Set(),
		capturedNonRemarquableSks: new Set(),
		capturedRemarquableArbreIdsByArr: new Map(),
	}
}

/*
Pour chaque palier Pokédex, set des sks actifs (identifiés, !unknownSpecies,
pokedexNumber dans [1..threshold]). Précalcul one-shot. Mirror Android
v1.4.0 BadgeEvaluator.buildPokedexTargets.
*/
function buildPokedexTargets(): Map<number, Set<number>>
{
	const vOut = new Map<number, Set<number>>()
	for (const vThreshold of kPokedexThresholds) vOut.set(vThreshold, new Set<number>())
	for (const vRow of allSpecies())
	{
		if (!isActiveSpecies(vRow.name)) continue
		const vN = vRow.name.pokedexNumber
		if (vN === null) continue
		for (const vThreshold of kPokedexThresholds)
		{
			if (vN <= vThreshold) vOut.get(vThreshold)!.add(vRow.sk)
		}
	}
	return vOut
}

/*
Pokédex v1.4.0 : palier N débloqué dès que toutes les espèces actives
avec pokedexNumber ∈ [1..N] sont capturées hors remarquables.
*/
function evaluatePokedex(
	inUnlocks: Map<string, number>,
	inTargets: ReadonlyMap<number, ReadonlySet<number>>,
	inCapturedNonRemarquable: ReadonlySet<number>,
	inTs: number,
): void
{
	for (const [vThreshold, vTarget] of inTargets)
	{
		if (vTarget.size === 0) continue
		let vCovered = true
		for (const vSk of vTarget) { if (!inCapturedNonRemarquable.has(vSk)) { vCovered = false; break } }
		if (vCovered) checkBinary(inUnlocks, pokedexBadgeId(vThreshold), true, inTs)
	}
}

function checkBinary(inMap: Map<string, number>, inBadgeId: string, inOk: boolean, inTs: number): void
{
	if (!inOk) return
	if (inMap.has(inBadgeId)) return
	inMap.set(inBadgeId, inTs)
}

/*
Record the first timestamp at which the running count for a
progressive badge crosses each of its tier thresholds.
*/
function checkProgressive(
	inCatalog: readonly BadgeDef[],
	inMap: Map<string, (number | null)[]>,
	inBadgeId: string,
	inCurrent: number,
	inTs: number,
): void
{
	const vDef = inCatalog.find((inD) => inD.id === inBadgeId)
	if (!vDef || vDef.kind !== 'progressive') return
	const vArr = inMap.get(inBadgeId)
	if (!vArr) return
	for (let vI = 0; vI < vDef.tiers.length; vI++)
	{
		if (vArr[vI] !== null) continue
		if (inCurrent >= vDef.tiers[vI]!.threshold) vArr[vI] = inTs
	}
}

function progressiveCount(inDef: ProgressiveBadgeDef, inAcc: Accumulator): number
{
	if (inDef.id === 'marcheur') return inAcc.count
	if (inDef.id === 'botaniste') return inAcc.species.size
	if (inDef.id === 'chasseur') return inAcc.remarquables.size
	return 0  // future progressives must map their accumulator here
}

/*
Convenience export — UI uses this to know "Implemented yet?" without
reaching back into the catalogue.
*/
export function isImplemented(inDef: BadgeDef): boolean { return inDef.implemented }
